import classNames from 'classnames';
import type { CSSProperties, Ref } from 'react';

import { useBrand } from '../../../../../shared/branding/brand-provider';
import flowStyles from '../ReportFlow.module.css';
import {
  ReportFlowBlockView,
  type ReportFlowLeafContentRenderer,
  renderLiveLeafContent,
} from '../ReportFlowBlockView';
import type { ReportFlowCoverParameter } from '../ReportFlowCoverParameters';
import type { ReportFlowBlock, ReportFlowDocument } from '../report-flow-types';
import styles from './ReportFlowPrint.module.css';
import type {
  PrintContentsEntry,
  PrintPageEntry,
} from './report-flow-print-layout';

const useCoverStyle = (): CSSProperties => {
  const brand = useBrand();
  return {
    '--print-cover-title-color': brand.coverTitleColor,
    '--print-cover-text-color': brand.coverDescriptionColor,
    ...(brand.coverBackgroundColor && {
      '--print-cover-background': brand.coverBackgroundColor,
    }),
  } as CSSProperties;
};

const CoverImage = () => {
  const { coverImagePortrait } = useBrand();
  if (!coverImagePortrait) return null;
  return <img className={styles.coverImage} src={coverImagePortrait} alt="" />;
};

export const ReportFlowPrintCover = ({
  doc,
  parameters,
}: {
  doc: ReportFlowDocument;
  parameters: ReportFlowCoverParameter[];
}) => {
  const brand = useBrand();
  return (
    <section
      className={classNames(styles.page, styles.darkPage)}
      style={useCoverStyle()}
      data-testid="report-flow-print-cover"
    >
      <CoverImage />
      <div className={styles.coverContent}>
        <img
          className={styles.coverLogo}
          src={brand.logos.reportLogo}
          alt={brand.displayName}
        />
        <h1 className={styles.coverTitle}>{doc.title}</h1>
        {doc.description && (
          <p className={styles.coverDescription}>{doc.description}</p>
        )}
        {parameters.length > 0 && (
          <dl className={styles.coverParameters}>
            {parameters.map((parameter) => (
              <div
                key={`${parameter.label}:${parameter.value}`}
                className={styles.coverParameter}
              >
                <dt className={styles.coverParameterLabel}>
                  {parameter.label}
                </dt>
                <dd className={styles.coverParameterValue}>
                  {parameter.value}
                </dd>
              </div>
            ))}
          </dl>
        )}
      </div>
    </section>
  );
};

export const ReportFlowPrintContents = ({
  entries,
}: {
  entries: PrintContentsEntry[];
}) => (
  <section
    className={classNames(styles.page, styles.darkPage)}
    style={useCoverStyle()}
    data-testid="report-flow-print-contents"
  >
    <CoverImage />
    <div className={styles.contentsContent}>
      <h2 className={styles.contentsTitle}>Contents</h2>
      <ol className={styles.contentsList}>
        {entries.map((entry) => (
          <li key={entry.anchorId} className={styles.contentsEntry}>
            <span>{entry.label}</span>
            <span className={styles.contentsPageNumber}>
              {entry.pageNumber}
            </span>
          </li>
        ))}
      </ol>
    </div>
  </section>
);

export const ReportFlowPrintPage = ({
  entries,
  pageNumber,
  footerLabel,
}: {
  entries: { block: ReportFlowBlock; slice?: PrintPageEntry['slice'] }[];
  pageNumber: number;
  footerLabel: string;
}) => (
  <section
    className={classNames(styles.page, styles.contentPage)}
    data-testid="report-flow-print-page"
  >
    <div className={classNames(styles.contentBody, flowStyles.printScope)}>
      {entries.map(({ block, slice }) =>
        slice ? (
          <div
            key={block.id}
            className={styles.slice}
            style={
              {
                '--print-slice-height': `${slice.heightPx}px`,
                '--print-slice-offset': `${slice.offsetPx}px`,
              } as CSSProperties
            }
          >
            <div className={styles.sliceContent}>
              <ReportFlowBlockView block={block} />
            </div>
          </div>
        ) : (
          <ReportFlowBlockView key={block.id} block={block} />
        )
      )}
    </div>
    <footer className={styles.footer}>
      <span>{pageNumber}</span>
      <span className={styles.footerTitle}>{footerLabel}</span>
    </footer>
  </section>
);

const renderMeasuredLeaf: ReportFlowLeafContentRenderer = (leaf) =>
  leaf.kind === 'text' ? renderLiveLeafContent(leaf) : null;

export const ReportFlowPrintMeasure = ({
  blocks,
  measureRef,
}: {
  blocks: ReportFlowBlock[];
  measureRef: Ref<HTMLDivElement>;
}) => (
  <div
    ref={measureRef}
    className={classNames(styles.measure, flowStyles.printScope)}
  >
    {blocks.map((block) => (
      <div key={block.id} data-print-block-id={block.id}>
        <ReportFlowBlockView
          block={block}
          renderLeafContent={renderMeasuredLeaf}
        />
      </div>
    ))}
  </div>
);
